import { Container, Graphics, Rectangle, Sprite, Texture, type Renderer } from 'pixi.js';
import { TILE_SIZE, type AvatarConfig, type Direction, type MapObject, type TileLayerName } from '@etown/shared';
import { HAIR_HEX, OUTFIT_HEX, SKIN_HEX } from '../../lib/avatarParts';
import { WORLD } from '../../lib/palette';
import type { AvatarVisual, TileTheme } from './TileTheme';

/** Aufloesungsfaktor beim Backen der Texturen (scharf bei Zoom > 1). */
const BAKE_RESOLUTION = 2;
const T = TILE_SIZE;

/**
 * "Soft Studio"-Flat-Design: alle Tiles werden einmalig als Vektorformen
 * (Graphics) gezeichnet und in Texturen gebacken — ab dann kostet ein Tile
 * genau so viel wie ein Spritesheet-Sprite.
 */
export class FlatTheme implements TileTheme {
  readonly name = 'flat';
  private textures = new Map<string, Texture>();
  private renderer: Renderer | null = null;

  async load(renderer: Renderer): Promise<void> {
    this.renderer = renderer;
    const bake = (id: string, draw: (g: Graphics) => void): void => {
      const g = new Graphics();
      draw(g);
      this.textures.set(id, renderer.generateTexture({ target: g, resolution: BAKE_RESOLUTION }));
      g.destroy();
    };

    // --- Boeden ---
    bake('floor:wood', (g) => {
      g.rect(0, 0, T, T).fill(WORLD.floorWood);
      g.rect(0, T * 0.33, T, 1).fill(WORLD.floorWoodLine);
      g.rect(0, T * 0.66, T, 1).fill(WORLD.floorWoodLine);
    });
    const carpets: Array<[string, number]> = [
      ['floor:carpet.blue', WORLD.floorCarpetBlue],
      ['floor:carpet.green', WORLD.floorCarpetGreen],
      ['floor:carpet.warm', WORLD.floorCarpetWarm],
    ];
    for (const [id, color] of carpets) {
      bake(id, (g) => {
        g.rect(0, 0, T, T).fill(WORLD.floorWood);
        g.rect(0, 0, T, T).fill({ color, alpha: 0.92 });
      });
    }
    bake('floor:stone', (g) => {
      g.rect(0, 0, T, T).fill(WORLD.floorStone);
      g.rect(0, 0, T, 1).fill({ color: 0xffffff, alpha: 0.35 });
    });
    // Konferenz-Teppich: unverkennbares Muster fuer Raum-Konferenz-Zonen
    bake('floor:carpet.conference', (g) => {
      g.rect(0, 0, T, T).fill(WORLD.floorConference);
      g.moveTo(0, T * 0.5).lineTo(T * 0.5, 0).stroke({ width: 1, color: WORLD.floorConferenceLine });
      g.moveTo(T * 0.5, T).lineTo(T, T * 0.5).stroke({ width: 1, color: WORLD.floorConferenceLine });
      g.rect(1, 1, T - 2, T - 2).stroke({ width: 1, color: WORLD.floorConferenceLine });
    });

    // --- Waende ---
    bake('walls:wall', (g) => {
      g.rect(0, 0, T, T).fill(WORLD.wall);
      g.rect(0, 0, T, T * 0.28).fill(WORLD.wallTop);
    });
    bake('walls:window', (g) => {
      g.rect(0, 0, T, T).fill(WORLD.wall);
      g.rect(0, 0, T, T * 0.28).fill(WORLD.wallTop);
      g.roundRect(T * 0.12, T * 0.34, T * 0.76, T * 0.5, 2).fill(WORLD.windowPane);
      g.rect(T * 0.12, T * 0.56, T * 0.76, 1.5).fill(WORLD.wall);
      g.rect(T * 0.48, T * 0.34, 1.5, T * 0.5).fill(WORLD.wall);
    });

    // --- Dekor ---
    bake('decor:plant', (g) => {
      g.roundRect(T * 0.3, T * 0.55, T * 0.4, T * 0.35, 3).fill(WORLD.plantPot);
      g.circle(T * 0.5, T * 0.38, T * 0.26).fill(WORLD.plantLeaf);
      g.circle(T * 0.32, T * 0.5, T * 0.15).fill(WORLD.plantLeaf);
      g.circle(T * 0.68, T * 0.5, T * 0.15).fill(WORLD.plantLeaf);
    });
    // Hohe Zimmerpflanze: schmaler Topf, gestapelte Blattkugeln
    bake('decor:plant.tall', (g) => {
      g.roundRect(T * 0.36, T * 0.62, T * 0.28, T * 0.3, 3).fill(WORLD.plantPot);
      g.circle(T * 0.5, T * 0.5, T * 0.18).fill(WORLD.plantLeaf);
      g.circle(T * 0.5, T * 0.32, T * 0.2).fill(WORLD.plantLeafDark);
      g.circle(T * 0.5, T * 0.16, T * 0.16).fill(WORLD.plantLeaf);
    });
    // Kaktus: Topf + gruene Kapsel mit zwei Aermchen
    bake('decor:plant.cactus', (g) => {
      g.roundRect(T * 0.34, T * 0.66, T * 0.32, T * 0.26, 3).fill(WORLD.plantPot);
      g.roundRect(T * 0.42, T * 0.24, T * 0.16, T * 0.46, T * 0.08).fill(WORLD.plantCactus);
      g.roundRect(T * 0.24, T * 0.4, T * 0.12, T * 0.1, T * 0.05).fill(WORLD.plantCactus);
      g.roundRect(T * 0.64, T * 0.34, T * 0.12, T * 0.1, T * 0.05).fill(WORLD.plantCactus);
      g.circle(T * 0.5, T * 0.24, T * 0.05).fill(0xff6b5e); // Bluete
    });
    // Schreibtisch mit Monitor+Tastatur. Der Monitor steht an der *hinteren*
    // Kante und schaut den Sitzenden an, die Tastatur liegt davor:
    // desk.down = Monitor an der Oberkante, die Person sitzt unterhalb;
    // desk.up = Monitor an der Unterkante, die Person sitzt oberhalb.
    const deskTop = (g: Graphics): void => {
      // fast raumfuellende, kantige Tischplatte -> wirkt wie ein echter Tisch
      g.rect(0, T * 0.08, T, T * 0.84).fill(WORLD.desk);
      g.rect(0, T * 0.08, T, 1.5).fill(WORLD.deskTop);
      g.rect(0, T * 0.9, T, 1.5).fill(WORLD.deskEdge);
    };
    bake('decor:desk.down', (g) => {
      deskTop(g);
      // Monitor an der Oberkante (Blickrichtung nach unten)
      g.roundRect(T * 0.3, T * 0.14, T * 0.4, T * 0.2, 2).fill(WORLD.monitor);
      g.roundRect(T * 0.34, T * 0.17, T * 0.32, T * 0.14, 1).fill(WORLD.monitorScreen);
      g.rect(T * 0.47, T * 0.34, T * 0.06, T * 0.05).fill(WORLD.monitor);
      // Tastatur davor
      g.roundRect(T * 0.28, T * 0.62, T * 0.44, T * 0.14, 2).fill(WORLD.keyboard);
    });
    bake('decor:desk.up', (g) => {
      deskTop(g);
      // Monitor an der Unterkante (Blickrichtung nach oben)
      g.roundRect(T * 0.3, T * 0.66, T * 0.4, T * 0.2, 2).fill(WORLD.monitor);
      g.roundRect(T * 0.34, T * 0.69, T * 0.32, T * 0.14, 1).fill(WORLD.monitorScreen);
      g.rect(T * 0.47, T * 0.61, T * 0.06, T * 0.05).fill(WORLD.monitor);
      g.roundRect(T * 0.28, T * 0.24, T * 0.44, T * 0.14, 2).fill(WORLD.keyboard);
    });
    // Um 90 Grad gedrehte Schreibtische: Platte fuellt das Tile senkrecht.
    // desk.left = Monitor an der linken Kante, die Person sitzt rechts davon;
    // desk.right = Monitor rechts, die Person sitzt links.
    const deskSide = (g: Graphics): void => {
      g.rect(0, T * 0.08, T, T * 0.84).fill(WORLD.desk);
      g.rect(0, T * 0.08, 1.5, T * 0.84).fill(WORLD.deskTop);
      g.rect(T * 0.9, T * 0.08, 1.5, T * 0.84).fill(WORLD.deskEdge);
    };
    bake('decor:desk.left', (g) => {
      deskSide(g);
      // Monitor an der linken Kante (Blickrichtung nach rechts)
      g.roundRect(T * 0.14, T * 0.3, T * 0.2, T * 0.4, 2).fill(WORLD.monitor);
      g.roundRect(T * 0.17, T * 0.34, T * 0.14, T * 0.32, 1).fill(WORLD.monitorScreen);
      g.rect(T * 0.34, T * 0.47, T * 0.05, T * 0.06).fill(WORLD.monitor);
      g.roundRect(T * 0.6, T * 0.28, T * 0.16, T * 0.44, 2).fill(WORLD.keyboard);
    });
    bake('decor:desk.right', (g) => {
      deskSide(g);
      // Monitor an der rechten Kante (Blickrichtung nach links)
      g.roundRect(T * 0.66, T * 0.3, T * 0.2, T * 0.4, 2).fill(WORLD.monitor);
      g.roundRect(T * 0.69, T * 0.34, T * 0.14, T * 0.32, 1).fill(WORLD.monitorScreen);
      g.rect(T * 0.61, T * 0.47, T * 0.05, T * 0.06).fill(WORLD.monitor);
      g.roundRect(T * 0.24, T * 0.28, T * 0.16, T * 0.44, 2).fill(WORLD.keyboard);
    });
    // WC-Trennwand (Stall-Divider): schmales senkrechtes Panel
    bake('decor:partition', (g) => {
      g.roundRect(T * 0.42, T * 0.04, T * 0.16, T * 0.92, 3).fill(WORLD.partition);
      g.rect(T * 0.42, T * 0.04, T * 0.16, 2).fill({ color: 0xffffff, alpha: 0.4 });
    });
    // Stuhl: Sitz plus Lehne. Die Lehne liegt der Blickrichtung gegenueber und
    // macht sie von oben lesbar — `chair.up` schaut nach oben, die Lehne sitzt
    // also an der Unterkante. Das richtungslose `chair` bleibt der reine Sitz;
    // aeltere und von Hand gebaute Karten kennen nur das.
    const chairSeat = (g: Graphics): void => {
      g.circle(T * 0.5, T * 0.5, T * 0.26).fill(WORLD.chair);
      g.circle(T * 0.5, T * 0.5, T * 0.16).fill({ color: 0xffffff, alpha: 0.25 });
    };
    bake('decor:chair', chairSeat);
    const chairLehne: Record<string, readonly [number, number, number, number]> = {
      // x, y, Breite, Hoehe der Lehne im Tile
      up: [T * 0.24, T * 0.74, T * 0.52, T * 0.14],
      down: [T * 0.24, T * 0.12, T * 0.52, T * 0.14],
      left: [T * 0.74, T * 0.24, T * 0.14, T * 0.52],
      right: [T * 0.12, T * 0.24, T * 0.14, T * 0.52],
    };
    for (const [richtung, [lx, ly, lw, lh]] of Object.entries(chairLehne)) {
      bake(`decor:chair.${richtung}`, (g) => {
        g.roundRect(lx, ly, lw, lh, 3).fill(WORLD.chairBack);
        chairSeat(g);
      });
    }
    bake('decor:table', (g) => {
      g.roundRect(T * 0.06, T * 0.06, T * 0.88, T * 0.88, 6).fill(WORLD.table);
      g.roundRect(T * 0.14, T * 0.14, T * 0.72, T * 0.72, 5).fill({ color: 0xffffff, alpha: 0.18 });
    });
    bake('decor:sofa', (g) => {
      g.roundRect(T * 0.05, T * 0.2, T * 0.9, T * 0.65, 7).fill(WORLD.sofa);
      g.roundRect(T * 0.12, T * 0.28, T * 0.76, T * 0.3, 5).fill(WORLD.sofaCushion);
    });
    bake('decor:rug', (g) => {
      g.roundRect(T * 0.05, T * 0.05, T * 0.9, T * 0.9, 10).fill({ color: WORLD.floorCarpetWarm, alpha: 0.9 });
    });
    bake('decor:bookshelf', (g) => {
      g.roundRect(T * 0.06, T * 0.08, T * 0.88, T * 0.84, 3).fill(WORLD.shelfWood);
      for (const shelfY of [T * 0.2, T * 0.55]) {
        g.rect(T * 0.12, shelfY, T * 0.76, T * 0.24).fill(WORLD.shelfBooks);
        // Buchruecken
        g.rect(T * 0.15, shelfY + 2, T * 0.08, T * 0.24 - 4).fill(0xff6b5e);
        g.rect(T * 0.27, shelfY + 2, T * 0.08, T * 0.24 - 4).fill(0x2ec4b6);
        g.rect(T * 0.39, shelfY + 2, T * 0.08, T * 0.24 - 4).fill(0x9b8cff);
        g.rect(T * 0.51, shelfY + 2, T * 0.1, T * 0.24 - 4).fill(0xf2b33d);
        g.rect(T * 0.66, shelfY + 2, T * 0.08, T * 0.24 - 4).fill(0x5aa9e6);
      }
    });
    bake('decor:cabinet', (g) => {
      g.roundRect(T * 0.08, T * 0.1, T * 0.84, T * 0.8, 3).fill(WORLD.cabinetFront);
      g.rect(T * 0.49, T * 0.14, 1.5, T * 0.72).fill({ color: 0x1e2430, alpha: 0.35 });
      g.circle(T * 0.42, T * 0.5, 1.8).fill(0x39414f);
      g.circle(T * 0.58, T * 0.5, 1.8).fill(0x39414f);
    });
    bake('decor:coffee', (g) => {
      g.roundRect(T * 0.22, T * 0.18, T * 0.56, T * 0.66, 4).fill(WORLD.coffeeDark);
      g.rect(T * 0.3, T * 0.28, T * 0.4, T * 0.12).fill({ color: WORLD.screenGlow, alpha: 0.8 });
      g.roundRect(T * 0.4, T * 0.58, T * 0.2, T * 0.16, 2).fill(0xffffff);
      g.circle(T * 0.5, T * 0.5, 1.6).fill(0xff6b5e);
    });
    // WC-Schuessel: Tank + Becken
    bake('decor:toilet', (g) => {
      g.roundRect(T * 0.32, T * 0.14, T * 0.36, T * 0.16, 2).fill(WORLD.porcelain);
      g.ellipse(T * 0.5, T * 0.56, T * 0.22, T * 0.26).fill(WORLD.porcelain);
      g.ellipse(T * 0.5, T * 0.56, T * 0.13, T * 0.16).fill(WORLD.porcelainShade);
    });
    // Waschbecken
    bake('decor:sink', (g) => {
      g.roundRect(T * 0.22, T * 0.28, T * 0.56, T * 0.44, 6).fill(WORLD.porcelain);
      g.ellipse(T * 0.5, T * 0.52, T * 0.18, T * 0.14).fill(WORLD.porcelainShade);
      g.rect(T * 0.47, T * 0.2, T * 0.06, T * 0.12).fill(0x8a96a8); // Armatur
    });
    // Serverschrank: dunkler Korpus mit LED-Reihe
    bake('decor:server', (g) => {
      g.roundRect(T * 0.14, T * 0.06, T * 0.72, T * 0.88, 3).fill(WORLD.serverBody);
      for (let i = 0; i < 5; i += 1) {
        g.rect(T * 0.22, T * (0.16 + i * 0.16), T * 0.56, T * 0.08).fill(WORLD.serverSlot);
        g.circle(T * 0.72, T * (0.2 + i * 0.16), 1.4).fill(i % 2 ? 0x3bb273 : 0x2ec4b6);
      }
    });
    // Getraenkekisten (gestapelt)
    bake('decor:crate', (g) => {
      g.roundRect(T * 0.1, T * 0.12, T * 0.8, T * 0.76, 2).fill(WORLD.crate);
      g.rect(T * 0.1, T * 0.5, T * 0.8, 2).fill({ color: 0x1e2430, alpha: 0.25 });
      for (const cx of [0.28, 0.5, 0.72]) {
        g.circle(T * cx, T * 0.31, T * 0.07).fill(WORLD.crateBottle);
        g.circle(T * cx, T * 0.69, T * 0.07).fill(WORLD.crateBottle);
      }
    });
    // Kuehlschrank
    bake('decor:fridge', (g) => {
      g.roundRect(T * 0.16, T * 0.06, T * 0.68, T * 0.88, 4).fill(WORLD.fridgeBody);
      g.rect(T * 0.16, T * 0.46, T * 0.68, 2).fill({ color: 0x1e2430, alpha: 0.2 });
      g.roundRect(T * 0.72, T * 0.14, T * 0.05, T * 0.24, 2).fill(0x8a96a8);
      g.roundRect(T * 0.72, T * 0.56, T * 0.05, T * 0.24, 2).fill(0x8a96a8);
    });
  }

  destroy(): void {
    for (const texture of this.textures.values()) texture.destroy(true);
    this.textures.clear();
    this.renderer = null;
  }

  createTile(typeId: string, layer: TileLayerName): Container | null {
    const texture = this.textures.get(`${layer}:${typeId}`);
    if (!texture) return null;
    const sprite = new Sprite(texture);
    sprite.width = T;
    sprite.height = T;
    return sprite;
  }

  /**
   * Objekte teilen sich Optik und Groesse: 11 Fenster derselben Abmessung
   * ergeben genau eine Textur. Gebacken wird erst hier und nicht in load(),
   * weil die Objektmasse erst mit der Karte bekannt sind.
   */
  createObject(obj: MapObject): Container {
    const root = new Container();
    const w = obj.w * T;
    const h = obj.h * T;
    const key = `object:${obj.type}:${w}x${h}`;

    let texture = this.textures.get(key);
    if (!texture) {
      const g = new Graphics();
      this.drawObject(g, obj.type, w, h);
      if (!this.renderer) {
        // Ohne Renderer laesst sich nichts backen — dann eben direkt zeichnen
        root.addChild(g);
        return root;
      }
      // Fester Rahmen ueber der ganzen Objektflaeche: ohne ihn schneidet
      // generateTexture auf die tatsaechlichen Formgrenzen zu, und Formen,
      // die nicht bei (0,0) beginnen (Kuehlschrank, Kaffeemaschine), saessen
      // danach versetzt.
      texture = this.renderer.generateTexture({
        target: g,
        resolution: BAKE_RESOLUTION,
        frame: new Rectangle(0, 0, w, h),
      });
      this.textures.set(key, texture);
      g.destroy();
    }

    const sprite = new Sprite(texture);
    sprite.width = w;
    sprite.height = h;
    root.addChild(sprite);
    return root;
  }

  /** Die reinen Formen — unveraendert, nur aus createObject herausgeloest. */
  private drawObject(g: Graphics, type: MapObject['type'], w: number, h: number): void {
    switch (type) {
      case 'whiteboard':
        g.roundRect(0, 0, w, h, 5).fill(WORLD.whiteboardFrame);
        g.roundRect(2, 2, w - 4, h - 4, 4).fill(WORLD.whiteboard);
        // angedeutete Marker-Striche
        g.moveTo(w * 0.15, h * 0.4).lineTo(w * 0.45, h * 0.4).stroke({ width: 2, color: 0xff6b5e });
        g.moveTo(w * 0.15, h * 0.62).lineTo(w * 0.6, h * 0.62).stroke({ width: 2, color: 0x2ec4b6 });
        break;
      case 'stickyboard':
        g.roundRect(0, 0, w, h, 5).fill(WORLD.stickyboard);
        g.rect(w * 0.12, h * 0.22, w * 0.14, h * 0.45).fill(0xfbe27a);
        g.rect(w * 0.38, h * 0.3, w * 0.14, h * 0.45).fill(0xff6b5e);
        g.rect(w * 0.64, h * 0.2, w * 0.14, h * 0.45).fill(0x9b8cff);
        break;
      case 'youtube':
        g.roundRect(0, 0, w, h, 5).fill(WORLD.screenDark);
        g.poly([w * 0.42, h * 0.28, w * 0.42, h * 0.72, w * 0.66, h * 0.5]).fill(WORLD.youtubeRed);
        break;
      case 'screen':
        g.roundRect(0, 0, w, h, 5).fill(WORLD.screenDark);
        g.rect(w * 0.1, h * 0.75, w * 0.8, 2).fill(WORLD.screenGlow);
        break;
      case 'link':
        g.circle(w / 2, h / 2, Math.min(w, h) * 0.4).fill(WORLD.linkKiosk);
        g.circle(w / 2, h * 0.32, 2).fill(0xffffff);
        g.roundRect(w / 2 - 1.5, h * 0.42, 3, h * 0.3, 2).fill(0xffffff);
        break;
      case 'picture':
        // Rahmen + Passepartout; das eigentliche Bild setzt der Renderer
        // als Sprite darueber (setObjectImage), sobald eines hochgeladen ist.
        g.roundRect(0, 0, w, h, 3).fill(WORLD.pictureFrame);
        g.rect(w * 0.1, h * 0.1, w * 0.8, h * 0.8).fill(WORLD.pictureMat);
        // Platzhalter-Motiv: Berge + Sonne
        g.poly([w * 0.18, h * 0.78, w * 0.42, h * 0.4, w * 0.6, h * 0.78]).fill(0x8a96a8);
        g.poly([w * 0.5, h * 0.78, w * 0.68, h * 0.5, w * 0.84, h * 0.78]).fill(0x7a8699);
        g.circle(w * 0.72, h * 0.28, Math.min(w, h) * 0.09).fill(0xf2b33d);
        break;
      case 'calendar':
        // 3-Monats-Wandkalender: blauer Kopf mit Jahr, darunter drei Monatsspalten
        g.roundRect(0, 0, w, h, 2).fill(WORLD.calendarPaper);
        g.rect(0, 0, w, h * 0.24).fill(0x2f4f8f); // blauer Kopf
        for (let m = 0; m < 3; m += 1) {
          const mx = w * (0.06 + m * 0.31);
          g.rect(mx, h * 0.3, w * 0.26, 1.5).fill(WORLD.calendarRed); // Monatslinie
          for (let row = 0; row < 3; row += 1) {
            for (let col = 0; col < 3; col += 1) {
              const dc = m * 3 + col === 6 ? WORLD.calendarRed : 0x39414f; // ein "Sonntag" rot
              g.circle(mx + w * (0.04 + col * 0.09), h * (0.44 + row * 0.16), 1).fill(dc);
            }
          }
        }
        break;
      case 'fridge':
        g.roundRect(w * 0.08, h * 0.04, w * 0.84, h * 0.92, 5).fill(WORLD.fridgeBody);
        g.rect(w * 0.08, h * 0.46, w * 0.84, 2).fill({ color: 0x1e2430, alpha: 0.2 });
        g.roundRect(w * 0.8, h * 0.12, w * 0.05, h * 0.24, 2).fill(0x8a96a8);
        g.roundRect(w * 0.8, h * 0.56, w * 0.05, h * 0.24, 2).fill(0x8a96a8);
        break;
      case 'coffee':
        g.roundRect(w * 0.18, h * 0.1, w * 0.64, h * 0.8, 5).fill(WORLD.coffeeDark);
        g.rect(w * 0.28, h * 0.24, w * 0.44, h * 0.14).fill({ color: WORLD.screenGlow, alpha: 0.85 });
        g.roundRect(w * 0.36, h * 0.62, w * 0.28, h * 0.16, 2).fill(0xffffff);
        g.circle(w * 0.5, h * 0.5, 2).fill(0xff6b5e);
        break;
      case 'arcade':
        g.roundRect(w * 0.1, h * 0.02, w * 0.8, h * 0.96, 4).fill(WORLD.arcadeCabinet);
        g.rect(w * 0.16, h * 0.08, w * 0.68, 2).fill({ color: WORLD.arcadeGlow, alpha: 0.85 }); // Marquee
        g.roundRect(w * 0.16, h * 0.12, w * 0.68, h * 0.3, 3).fill(WORLD.arcadeBody);
        g.rect(w * 0.22, h * 0.16, w * 0.56, h * 0.2).fill(WORLD.arcadeScreen); // Bildschirm
        g.roundRect(w * 0.16, h * 0.5, w * 0.68, h * 0.18, 3).fill(WORLD.arcadeBody); // Bedienpult
        g.circle(w * 0.34, h * 0.59, Math.min(w, h) * 0.03).fill(WORLD.arcadeGlow);
        g.circle(w * 0.5, h * 0.59, Math.min(w, h) * 0.03).fill(0xf2b33d);
        g.circle(w * 0.66, h * 0.59, Math.min(w, h) * 0.03).fill(0x2ec4b6);
        break;
      case 'boardgame': {
        g.roundRect(0, 0, w, h, 6).fill(WORLD.boardFrame);
        const inset = Math.min(w, h) * 0.12;
        const bw = w - inset * 2;
        const bh = h - inset * 2;
        const n = 4; // angedeutetes 4x4-Karo
        for (let r = 0; r < n; r += 1) {
          for (let c = 0; c < n; c += 1) {
            g.rect(inset + (bw / n) * c, inset + (bh / n) * r, bw / n, bh / n)
              .fill((r + c) % 2 ? WORLD.boardDark : WORLD.boardLight);
          }
        }
        break;
      }
    }
  }

  /**
   * Kleine Chibi-Figur (Kopf, Torso, Arme, Beine) — steht auf dem Anker
   * (Fuesse bei y=0, Koerper waechst nach oben, Gather-Style). Alles reine
   * Vektorformen; pro Frame aendern sich nur Transforms (Bein-/Arm-Schwung,
   * Hopser), neu gezeichnet wird nur beim Richtungswechsel.
   */
  createAvatar(config: AvatarConfig): AvatarVisual {
    const shirt = OUTFIT_HEX[config.shirtColor];
    const pantsColor = OUTFIT_HEX[config.pantsColor];
    const skin = SKIN_HEX[config.skin];
    const hairColor = HAIR_HEX[config.hairColor];
    const INK = 0x1e2430;
    const HC_Y = -32; // Kopfmitte
    const HC_R = 9.5;

    const view = new Container();
    const shadow = new Graphics().ellipse(0, 1, 8, 3.5).fill({ color: WORLD.shadow, alpha: 0.18 });

    const figure = new Container(); // hopst beim Laufen (nur y)
    const speakingRing = new Graphics().circle(0, HC_Y, 13).stroke({ width: 3, color: WORLD.screenGlow });
    speakingRing.visible = false;

    const flip = new Container(); // scale.x = -1 fuer 'left' (Seitenansicht gespiegelt)

    // Beine: Graphics einmal gezeichnet, Container an der Huefte -> Rotation = Schwung
    const makeLeg = (): Container => {
      const c = new Container();
      c.addChild(new Graphics().roundRect(-2.5, 0, 5, 9, 2.5).fill(pantsColor));
      return c;
    };
    const legL = makeLeg();
    const legR = makeLeg();

    const torso = new Graphics();

    // Arme: Aermel (Oberteilfarbe) + Hand (Hautton), Container an der Schulter
    const makeArm = (): Container => {
      const c = new Container();
      const g = new Graphics();
      g.roundRect(-2, -1, 4, 11, 2).fill(shirt);
      g.circle(0, 10, 2).fill(skin);
      c.addChild(g);
      return c;
    };
    const armL = makeArm();
    const armR = makeArm();

    const head = new Graphics().circle(0, HC_Y, HC_R).fill(skin);
    const hair = new Graphics();
    const eyes = new Graphics();
    const accessory = new Graphics();

    // z-Reihenfolge: Beine -> Torso -> Arme -> Kopf -> Haar/Augen/Accessoire
    flip.addChild(legL, legR, torso, armL, armR, head, hair, eyes, accessory);
    figure.addChild(speakingRing, flip);
    view.addChild(shadow, figure);

    type ViewKind = 'front' | 'back' | 'side';
    const viewOf = (dir: Direction): ViewKind =>
      dir === 'down' ? 'front' : dir === 'up' ? 'back' : 'side';

    const drawTorso = (v: ViewKind): void => {
      torso.clear();
      const halfW = v === 'side' ? 6.5 : 8;
      torso
        .roundRect(-halfW, -24, halfW * 2, 15, 6)
        .fill(shirt)
        .roundRect(-halfW, -24, halfW * 2, 15, 6)
        .stroke({ width: 2.5, color: 0xffffff });
    };

    const drawEyes = (v: ViewKind): void => {
      eyes.clear();
      if (v === 'back') return;
      const pts = v === 'side' ? [2, 6.5] : [-3, 3];
      for (const ex of pts) eyes.circle(ex, -33.5, 1.8).fill(INK);
    };

    const drawHair = (v: ViewKind): void => {
      hair.clear();
      if (config.hair === 'glatze') return;
      const sx = v === 'side' ? 1 : 0; // Seitenansicht leicht nach vorne
      // Rueckansicht: erst den ganzen Hinterkopf fuellen
      if (v === 'back' && config.hair !== 'irokese') {
        hair.circle(0, HC_Y, HC_R + 0.5).fill(hairColor);
      }
      switch (config.hair) {
        case 'kurz':
          hair.arc(sx, HC_Y, 10, Math.PI, Math.PI * 2).fill(hairColor);
          break;
        case 'bob':
          hair.arc(sx, HC_Y, 10, Math.PI, Math.PI * 2).fill(hairColor);
          hair.roundRect(sx - 10, -34, 3.5, 12, 1.75).fill(hairColor);
          hair.roundRect(sx + 6.5, -34, 3.5, 12, 1.75).fill(hairColor);
          break;
        case 'dutt':
          hair.arc(sx, HC_Y, 10, Math.PI, Math.PI * 2).fill(hairColor);
          hair.circle(v === 'side' ? -6.5 : 0, v === 'side' ? -41 : -44, 3).fill(hairColor);
          break;
        case 'locken': {
          const blobs: Array<[number, number]> = [
            [-8.2, -34.5], [-5, -39.5], [0, -41], [5, -39.5], [8.2, -34.5],
          ];
          if (v === 'back') { blobs.push([-8.2, -29.5], [8.2, -29.5]); }
          for (const [bx, by] of blobs) hair.circle(sx + bx, by, 3.4).fill(hairColor);
          break;
        }
        case 'irokese':
          // Gefuellter Kamm mittig auf dem Kopf — bewusst KEIN gestrichelter
          // Bogen: arc().stroke() ohne moveTo zieht sonst eine Linie vom
          // Pfad-Ursprung (Fuesse) zum Kopf (Streifen durch den Koerper).
          hair.poly([sx - 3, -40, sx - 2, -47, sx, -49, sx + 2, -47, sx + 3, -40]).fill(hairColor);
          break;
      }
    };

    const drawAccessory = (v: ViewKind): void => {
      accessory.clear();
      if (config.accessory === 'keins') return;
      const hx = v === 'side' ? 1 : 0;
      switch (config.accessory) {
        case 'brille':
        case 'sonnenbrille': {
          if (v === 'back') return;
          const pts = v === 'side' ? [2, 6.5] : [-3, 3];
          const filled = config.accessory === 'sonnenbrille';
          for (const ex of pts) {
            if (filled) accessory.circle(ex, -33.5, 3).fill(INK);
            else accessory.circle(ex, -33.5, 3).stroke({ width: 1.4, color: INK });
          }
          accessory.moveTo(pts[0]! + 1, -33.5).lineTo(pts[1]! - 1, -33.5).stroke({ width: 1.4, color: INK });
          break;
        }
        case 'hut':
          accessory.ellipse(hx, -38, 12, 2.5).fill(0x39414f);
          accessory.roundRect(hx - 6.5, -47, 13, 8, 3).fill(0x39414f);
          break;
        case 'muetze':
          accessory.arc(hx, -33, 10.2, Math.PI, Math.PI * 2).fill(0xe0454e);
          accessory.circle(hx, -44.5, 2.2).fill(0xffffff);
          break;
        case 'kopfhoerer':
          accessory.arc(0, HC_Y, 10.5, Math.PI * 1.1, Math.PI * 1.9).stroke({ width: 2.4, color: INK });
          accessory.roundRect(-10.5 - 1.7, -35.5, 3.4, 7, 2).fill(INK);
          accessory.roundRect(10.5 - 1.7, -35.5, 3.4, 7, 2).fill(INK);
          break;
      }
    };

    let lastDirection: Direction | null = null;
    const drawPose = (dir: Direction): void => {
      const v = viewOf(dir);
      flip.scale.x = dir === 'left' ? -1 : 1;
      drawTorso(v);
      drawEyes(v);
      drawHair(v);
      drawAccessory(v);
      // Bein-/Arm-Positionen je Ansicht (Rotation animiert um diese Punkte)
      if (v === 'side') {
        legL.position.set(-2, -9);
        legR.position.set(2, -9);
        armL.position.set(0, -22);
        armR.visible = false;
      } else {
        legL.position.set(-3.5, -9);
        legR.position.set(3.5, -9);
        armL.position.set(-9.5, -22);
        armR.position.set(9.5, -22);
        armR.visible = true;
      }
    };
    drawPose('down');
    lastDirection = 'down';

    let phase = 0;
    return {
      view,
      setPose: (direction, moving) => {
        if (direction !== lastDirection) {
          drawPose(direction);
          lastDirection = direction;
        }
        if (moving) {
          phase += 0.3;
          const s = Math.sin(phase);
          legL.rotation = s * 0.35;
          legR.rotation = -s * 0.35;
          armL.rotation = -s * 0.25;
          armR.rotation = s * 0.25;
          figure.position.y = -Math.abs(s) * 1.2;
        } else {
          phase = 0;
          legL.rotation = legR.rotation = armL.rotation = armR.rotation = 0;
          figure.position.y = 0;
        }
      },
      setSpeaking: (speaking) => {
        speakingRing.visible = speaking;
      },
    };
  }

  catalog(): ReadonlyArray<{ group: string; layer: TileLayerName; typeIds: readonly string[] }> {
    return [
      {
        group: 'Boeden',
        layer: 'floor',
        typeIds: ['wood', 'carpet.blue', 'carpet.green', 'carpet.warm', 'carpet.conference', 'stone'],
      },
      { group: 'Waende', layer: 'walls', typeIds: ['wall', 'window'] },
      {
        group: 'Moebel',
        layer: 'decor',
        typeIds: [
          'desk.down', 'desk.up', 'desk.left', 'desk.right',
          'chair.down', 'chair.up', 'chair.left', 'chair.right', 'table', 'sofa',
          'plant', 'plant.tall', 'plant.cactus', 'rug', 'bookshelf', 'cabinet', 'coffee', 'fridge',
        ],
      },
      {
        group: 'Sanitaer & Technik',
        layer: 'decor',
        typeIds: ['toilet', 'sink', 'partition', 'server', 'crate'],
      },
    ];
  }
}
